// ---------------------------------------------------------------------------
// Global axios response interceptor (mobile web)
//
// Goal: when ANY API call returns 401 (server JWT revoked / expired by the
// backend's ~10-minute key rotation), silently regenerate the server JWT
// and replay the original request — so users never get bounced back to
// Auth0 mid-session unless they really have been logged out.
//
// Mobile is Auth0-only (no HSLogin / setupadmin path), so this is a
// simpler version of the web interceptor in notify-web-code/src/lib/
// axiosInterceptor.ts. The Auth0 path here mirrors the web one exactly so
// behavior stays consistent across platforms:
//
//   getTokenSilently()  →  generatecustomtokeninauth0  →  new server JWT
//
// Flow per failed request:
//   1. Skip if not 401, already retried, or the failing call is itself
//      the regen endpoint or an Auth0 endpoint (avoid infinite loops).
//   2. Coalesce concurrent 401s onto a single in-flight regen promise so
//      10 simultaneous failures result in ONE regen call, then all 10
//      replay with the same new token.
//   3. Replay the original request with the new Bearer token.
//   4. If recovery genuinely fails AND Auth0 says the session is dead
//      (login_required / missing_refresh_token / invalid_grant), force
//      logout to /auth0login (RESET_APP_STATE + router.push). For any
//      other failure (network blip, mfa_required, consent_required,
//      generic timeout, backend returning empty token, etc.) we just
//      reject the request and leave the user logged in — the next API
//      call will try regen again. The Auth0 session itself is still alive
//      in those cases, so kicking the user out would be wrong.
// ---------------------------------------------------------------------------

import axios from "axios";
import { Store } from "vuex";
import router from "@/router";
import { getFreshAuth0AccessToken, getAuth0User } from "./auth0";

let regenPromise: Promise<string> | null = null;
// Single-flight guard for the hard-logout flow: if 5 concurrent 401s all
// fail to regen, we only want ONE redirect to /auth0login, not five.
let logoutInFlight = false;

const isAuth0RegenCall = (cfg: any): boolean => {
  try {
    const url: string = (cfg && cfg.url) || "";
    return url.includes("generatecustomtokeninauth0");
  } catch (_e) {
    return false;
  }
};

const isAuth0NetworkCall = (cfg: any): boolean => {
  try {
    const url: string = (cfg && cfg.url) || "";
    // Anything pointing at the Auth0 tenant itself.
    return url.includes(".auth0.com") || url.includes("/oauth/token");
  } catch (_e) {
    return false;
  }
};

function forceLogoutToAuth0(store: Store<any>): void {
  if (logoutInFlight) return;
  logoutInFlight = true;
  try {
    store.commit("RESET_APP_STATE");
  } catch (_e) {
    /* ignore */
  }
  try {
    window.localStorage.removeItem("access_token");
  } catch (_e) {
    /* ignore */
  }
  try {
    if (router.currentRoute?.value?.path !== "/auth0login") {
      router.replace("/auth0login");
    }
  } catch (_e) {
    /* ignore */
  }
  // Don't reset logoutInFlight — the page is going away anyway. Leaving
  // it true prevents stragglers from re-triggering on the dying page.
}

async function regenerateServerToken(store: Store<any>): Promise<string> {
  // 1. Refresh the Auth0 access_token (throws if Auth0 session is dead).
  await getFreshAuth0AccessToken();

  // 2. Get the user's email — backend keys the new server JWT off it.
  const user = await getAuth0User();
  if (!user || !user.email) {
    const err: any = new Error("no_auth0_user");
    err.code = "no_auth0_user";
    throw err;
  }

  // 3. Ask the backend for a fresh server JWT. The action uses the freshly
  //    refreshed Auth0 access_token (NOT the possibly-revoked server JWT)
  //    so this still works after the server has invalidated the old JWT.
  const newToken: string = await store.dispatch("generatecustomtokeninauth0", {
    email: user.email,
  });
  if (!newToken || typeof newToken !== "string") {
    const err: any = new Error("regen_returned_empty_token");
    err.code = "regen_returned_empty_token";
    throw err;
  }

  // 4. Persist it everywhere the rest of the app reads from.
  store.commit("LOGIN_SUCCESS", { data: { token: newToken } });
  return newToken;
}

export function setupAxiosInterceptor(store: Store<any>): void {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config || {};

      // Bail out on anything that isn't a recoverable 401.
      if (status !== 401) return Promise.reject(error);
      if (original._retried) return Promise.reject(error);
      if (isAuth0RegenCall(original)) return Promise.reject(error);
      if (isAuth0NetworkCall(original)) return Promise.reject(error);

      try {
        // Share a single in-flight regen across concurrent 401s.
        if (!regenPromise) {
          regenPromise = regenerateServerToken(store).then(
            (tok) => {
              regenPromise = null;
              return tok;
            },
            (e) => {
              regenPromise = null;
              throw e;
            },
          );
        }
        const newToken = await regenPromise;

        // Replay the original request with the new token. Mark _retried
        // so we don't loop forever if the same request 401s again.
        original._retried = true;
        original.headers = original.headers || {};
        original.headers.Authorization = "Bearer " + newToken;
        // Keep the global default in sync so any subsequent code reading
        // axios.defaults.headers.common (the codebase sets this on every
        // action) picks up the new token.
        axios.defaults.headers.common.Authorization = "Bearer " + newToken;

        return axios(original);
      } catch (regenErr: any) {
        // Decide whether this failure means "Auth0 session is really dead,
        // log out" or "transient/unrecoverable-but-not-user-facing, just
        // reject". Auth0 reports many recoverable conditions through
        // getTokenSilently — none of those mean session dead, so logging
        // the user out for them is wrong.
        const code: string =
          (regenErr &&
            (regenErr.error || regenErr.code || regenErr.errorCode || regenErr.message)) ||
          "";

        const isAuth0Dead =
          code === "login_required" ||
          code === "missing_refresh_token" ||
          code === "invalid_grant";

        // Suppress logout entirely while the user is mid-auth (sitting on
        // the login page). LoginView.vue runs its own getTokenSilently /
        // handleRedirectCallback flow there and a forced redirect would
        // race with it.
        const path = (window.location.pathname || "").toLowerCase();
        const inAuthFlow = path === "/auth0login";

        try {
          console.warn("[auth-regen] Token regen failed", {
            errorObject: regenErr,
            errorString: String(regenErr),
            parsedCode: code,
            auth0Error: regenErr && regenErr.error,
            auth0ErrorDescription: regenErr && regenErr.error_description,
            isAuth0Dead,
            inAuthFlow,
            willForceLogout: isAuth0Dead && !inAuthFlow,
            originalRequestUrl: original && original.url,
            originalRequestMethod: original && original.method,
          });
        } catch (_e) {
          /* never let logging itself throw */
        }

        if (isAuth0Dead && !inAuthFlow) {
          forceLogoutToAuth0(store);
        }
        // For everything else (network blip, timeout, mfa_required,
        // generic backend error, empty token returned by backend, etc.)
        // leave the user logged in — Auth0 session is still alive.
        return Promise.reject(error);
      }
    },
  );
}
